import { motion } from "framer-motion";
import { resolveImage } from "../lib/data";
import { Fragment } from "react";
import SplitText from "./SplitText";

/**
 * Rende un testo con supporto a:
 *  - "\n" → <br/>
 *  - "*text*" → <em className="font-serif italic text-[#C48F56]">
 * Usato dai titoli hero gestiti da CMS.
 */
export function renderRichTitle(text) {
  if (!text) return null;
  // split by newline first
  const lines = String(text).split(/\n/);
  return lines.map((line, li) => {
    const parts = line.split(/(\*[^*]+\*)/g).filter(Boolean);
    return (
      <Fragment key={li}>
        {li > 0 && <br />}
        {parts.map((p, pi) => {
          if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
            return (
              <span
                key={pi}
                className="font-serif italic text-[#C48F56]"
                style={{ fontWeight: 400 }}
              >
                {p.slice(1, -1)}
              </span>
            );
          }
          return <Fragment key={pi}>{p}</Fragment>;
        })}
      </Fragment>
    );
  });
}

export default function PageHero({ eyebrow, title, subtitle, image }) {
  return (
    <section
      data-testid="page-hero"
      className="relative h-[75svh] min-h-[540px] w-full overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${resolveImage(image)})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-[#08080A]" />
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {eyebrow && (
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/60">
              <span className="h-px w-10 bg-[#C48F56]" />
              {eyebrow}
            </div>
          )}
          {typeof title === "string" ? (
            <SplitText
              as="h1"
              className="mt-6 font-display text-balance tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-5xl leading-[1.05] font-medium"
              text={title}
              delay={0.15}
              data-testid="page-hero-title"
            />
          ) : (
            <h1 className="mt-6 font-display text-balance tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-5xl leading-[1.05] font-medium">
              {title}
            </h1>
          )}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed"
            >
              {subtitle}
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
